import * as ents from '@attain-apps/entities'
import type cytoscape from 'cytoscape'

import type * as models from '../models'

/**
 * Produces {@link cytoscape.ElementsDefinition} from {@link models.Data}
 */
export function getElements({
  nodes,
  edges,
}: models.Data): cytoscape.ElementsDefinition {
  // Compute sibling order for each node based on edge ordering
  // Group edges by parent, then assign order based on position in that group
  const siblingOrders = computeSiblingOrders(edges)

  return {
    edges: edges.map(edge => ({
      data: getEdgeDataDefinition(edge),
      group: 'edges',
    })),
    nodes: nodes.map(node => ({
      data: getNodeDataDefinition(node, siblingOrders),
      group: 'nodes',
    })),
  }
}

/**
 * Computes the sibling order for each node based on edge ordering.
 * Children of the same parent are ordered by their position in the edges array.
 */
function computeSiblingOrders(
  edges: readonly models.Edge[],
): Map<string, number> {
  const siblingOrders = new Map<string, number>()
  const childrenByParent = new Map<string, string[]>()

  // Group children by parent, preserving edge order
  for (const edge of edges) {
    const children = childrenByParent.get(edge.parentId) ?? []
    children.push(edge.childId)
    childrenByParent.set(edge.parentId, children)
  }

  // Assign sibling order within each parent group
  for (const children of childrenByParent.values()) {
    children.forEach((childId, index) => {
      siblingOrders.set(childId, index)
    })
  }

  return siblingOrders
}

/**
 * Converts a {@link models.Edge} into a {@link cytoscape.EdgeDataDefinition}
 */
export function getEdgeDataDefinition({
  childId,
  parentId,
  leaderType,
}: models.Edge): cytoscape.EdgeDataDefinition {
  return {
    id: `${parentId}->${childId}`,
    leaderType,
    source: parentId,
    target: childId,
  }
}

/**
 * Converts a {@link models.Node} into a {@link cytoscape.NodeDataDefinition}
 */
export function getNodeDataDefinition(
  node: Partial<ents.core.display.tree.Node>,
  siblingOrders?: Map<string, number>,
): cytoscape.NodeDataDefinition {
  const id = ents.core.display.tree.Node.Id.get(node)
  // NOTE: we need to at least create a shallow copy since properties may get
  // overwritten in Cytoscape
  return {
    id,
    ...node,
    parent: undefined,
    // Store sibling order for use in layout sorting
    siblingOrder: siblingOrders?.get(id) ?? 0,
  }
}
